import { ApiRouteService } from 'core/api';

import { HttpClient } from './HttpClient';

export interface CreateTextNoteRequest {
  patientId: number;
  examId?: number;
  content: string;
  title?: string;
  description?: string;
  tagIds?: number[];
  captureTime?: Date;
  eventTime?: Date;
  categoryId?: number;
}

export interface CreateTextNoteResponse {
  fileId: number;
  fileName: string;
  success: boolean;
  message: string;
}

export interface TranscribeAudioRequest {
  createTextNote?: boolean;
  tagIds?: number[];
  language?: string;
}

export interface TranscribeAudioResponse {
  success: boolean;
  transcribedText: string;
  duration?: number; // Duration in seconds
  language?: string;
  confidence?: number;
  createdNoteFileId?: number;
  createdNoteFileName?: string;
}

export interface ProcessAudioRequest {
  promptTemplate?: string;
}

export interface ProcessAudioResponse {
  success: boolean;
  processedContent: string;
  originalFileId: number;
}

export class NotesClient {
  constructor(private httpClient: HttpClient) {
    this.createTextNote = this.createTextNote.bind(this);
    this.transcribeAudio = this.transcribeAudio.bind(this);
    this.processAudioWithAI = this.processAudioWithAI.bind(this);
  }

  /**
   * Creates a text note for a patient
   */
  public async createTextNote(request: CreateTextNoteRequest, authMode: 'msal-required' | 'share-required' = 'msal-required'): Promise<CreateTextNoteResponse> {
    const options = await this.httpClient.createStandardOptions('POST', request, { authMode });

    const response = await this.httpClient.fetchAndParse<CreateTextNoteResponse>(
      fetch(`${ApiRouteService.getCompumedApiBaseRoute()}/api/notes/create-text-note`, options),
    );

    return response;
  }

  /**
   * Transcribes an audio file to text
   */
  public async transcribeAudio(
    fileId: number,
    request?: TranscribeAudioRequest,
    authMode: 'msal-required' | 'share-required' = 'msal-required',
  ): Promise<TranscribeAudioResponse> {
    const options = await this.httpClient.createStandardOptions('POST', request || {}, { authMode });

    const response = await this.httpClient.fetchAndParse<TranscribeAudioResponse>(
      fetch(`${ApiRouteService.getCompumedApiBaseRoute()}/api/notes/${fileId}/transcribe`, options),
    );

    return response;
  }

  /**
   * Processes an audio file with AI analysis after transcription
   */
  public async processAudioWithAI(
    fileId: number,
    request: ProcessAudioRequest,
    authMode: 'msal-required' | 'share-required' = 'msal-required',
  ): Promise<ProcessAudioResponse> {
    const options = await this.httpClient.createStandardOptions('POST', request, { authMode });

    const response = await this.httpClient.fetchAndParse<ProcessAudioResponse>(
      fetch(`${ApiRouteService.getCompumedApiBaseRoute()}/api/notes/${fileId}/process-audio`, options),
    );

    return response;
  }
}
